/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useEffect, useState } from "react";
import { get, ref } from "firebase/database";
import { App as CapApp } from "@capacitor/app";
import * as Sentry from "@sentry/react";
import { db } from "@/config/firebase";
import {
  evaluateForceUpdate,
  parseBuildNumber,
  type ForceUpdateDecision,
} from "@/utils/forceUpdateGate";

const CONFIG_READ_TIMEOUT_MS = 2500;

const NO_MODAL: ForceUpdateDecision = { show: false, dismissible: false };

/**
 * Launch-time build-version gate. Reads the installed native build number via
 * `App.getInfo()` and the live `/config/global` doc from RTDB, then defers to
 * the pure `evaluateForceUpdate` decision.
 *
 * Non-blocking + fail-open: the hook returns NO_MODAL until the check
 * resolves, so the app UI renders normally while config loads. ANY failure —
 * getInfo rejects (web/test), RTDB read errors, the read exceeds
 * CONFIG_READ_TIMEOUT_MS, or the config is missing/malformed — resolves to
 * NO_MODAL (logged to Sentry, never blocking).
 *
 * Mirrors the SOS kill-switch hook (useSosEnabled), but one-shot on launch
 * rather than a live subscription.
 */
export function useForceUpdateGate(): ForceUpdateDecision {
  const [decision, setDecision] = useState<ForceUpdateDecision>(NO_MODAL);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const info = await CapApp.getInfo();
        const installedBuild = parseBuildNumber(info.build);

        // Read /config/global, bounded by a hard timeout. Promise.race means a
        // hung RTDB read can never keep the gate pending past the budget.
        const snap = await Promise.race([
          get(ref(db, "config/global")),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("config read timeout")), CONFIG_READ_TIMEOUT_MS),
          ),
        ]);

        const config = snap.exists() ? (snap.val() as unknown) : null;
        if (cancelled) return;
        setDecision(evaluateForceUpdate(installedBuild, config));
      } catch (err) {
        // Fail-open: getInfo unavailable (web/test), read error, or timeout.
        // Log for visibility but never trap the user.
        Sentry.captureException(err, { tags: { feature: "force-update-gate" } });
        if (!cancelled) setDecision(NO_MODAL);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return decision;
}
